import React, { useState, useEffect, useRef } from "react";
import "./petforms.css";
import { useTranslation } from "react-i18next";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";

const VaccinationForm = ({ formData, setFormData, onSave, onCancel, formMode, petData, userTypeColor = "#8548A8" }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n?.dir?.() === "rtl";
  
  // Apply user type color to Flatpickr calendars
  useFlatpickrUserType();
  
  // Refs for date inputs
  const dateAdministeredRef = useRef(null);
  const nextDueDateRef = useRef(null);
  const petLabel = petData
    ? `${petData.species} / ${petData.breed} – ${petData.age}`
    : "";

  // Helpers to keep UI keys + API keys in sync
  const setBoth = (uiKey, apiKey) => (val) =>
    setFormData({
      ...formData,
      [uiKey]: val,
      [apiKey]: val,
    });

  // Values prefer API snake_case, fallback to existing UI keys
  const v = {
    veterinarian: formData.veterinarian ?? formData.Veterinarian ?? "",
    vaccine_name: formData.vaccine_name ?? formData["Vaccine Name"] ?? "",
    date_administered: formData.date_administered ?? formData["Date Administered"] ?? "",
    next_due_date: formData.next_due_date ?? formData["Next Due Date"] ?? "",
    note: formData.note ?? formData.Note ?? "",
  };

  const [errors, setErrors] = useState({});

  const handleSubmit = async () => {
    // Client-side validation
    const newErrors = {};
    if (!v.vaccine_name || v.vaccine_name.trim() === '') newErrors.vaccine_name = ['The vaccine name field is required.'];
    if (!v.date_administered || v.date_administered === '') newErrors.date_administered = ['The date administered field is required.'];
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      // onSave is provided by parent and may throw server validation errors
      await onSave();
    } catch (err) {
      // If server returned validation errors in standard shape, show them
      if (err && err.errors) {
        setErrors(err.errors);
      } else if (err && err.message) {
        setErrors({ _global: [err.message] });
      }
    }
  }

  const getHoverColor = (color) => {
    if (color === "#8548A8") return "#7A4397";
    if (color === "#00D4B3") return "#00C4A7";
    if (color === "#D65745") return "#C7493A";
    if (color === "#2A8AD2") return "#2596BE";
    return color;
  };

  // Initialize Flatpickr for date inputs (only once on mount)
  useEffect(() => {
    let dateAdministeredPicker = null;
    let nextDueDatePicker = null;

    if (dateAdministeredRef.current) {
      dateAdministeredPicker = flatpickr(dateAdministeredRef.current, {
        dateFormat: "Y-m-d",
        allowInput: true,
        clickOpens: true,
        defaultDate: v.date_administered || null,
        onChange: function(selectedDates, dateStr) {
          // Use functional update to preserve existing formData
          setFormData(prev => ({
            ...prev,
            "Date Administered": dateStr,
            date_administered: dateStr,
          }));
        },
        onReady: function(selectedDates, dateStr, instance) {
          instance.calendarContainer.style.fontFamily = '"Red Hat Display", sans-serif';
        }
      });
    }

    if (nextDueDateRef.current) {
      nextDueDatePicker = flatpickr(nextDueDateRef.current, {
        dateFormat: "Y-m-d",
        allowInput: true,
        clickOpens: true,
        defaultDate: v.next_due_date || null,
        onChange: function(selectedDates, dateStr) {
          // Use functional update to preserve existing formData
          setFormData(prev => ({
            ...prev,
            "Next Due Date": dateStr,
            next_due_date: dateStr,
          }));
        },
        onReady: function(selectedDates, dateStr, instance) {
          instance.calendarContainer.style.fontFamily = '"Red Hat Display", sans-serif';
        }
      });
    }

    return () => {
      // Safely destroy dateAdministeredPicker
      if (dateAdministeredRef.current && dateAdministeredRef.current._flatpickr) {
        try {
          dateAdministeredRef.current._flatpickr.destroy();
        } catch (error) {
          console.warn('Error destroying dateAdministeredPicker:', error);
        }
      } else if (dateAdministeredPicker && typeof dateAdministeredPicker.destroy === 'function') {
        try {
          dateAdministeredPicker.destroy();
        } catch (error) {
          console.warn('Error destroying dateAdministeredPicker:', error);
        }
      }
      
      // Safely destroy nextDueDatePicker
      if (nextDueDateRef.current && nextDueDateRef.current._flatpickr) {
        try {
          nextDueDateRef.current._flatpickr.destroy();
        } catch (error) {
          console.warn('Error destroying nextDueDatePicker:', error);
        }
      } else if (nextDueDatePicker && typeof nextDueDatePicker.destroy === 'function') {
        try {
          nextDueDatePicker.destroy();
        } catch (error) {
          console.warn('Error destroying nextDueDatePicker:', error);
        }
      }
    };
  }, []); // Only run once on mount

  // Update Flatpickr dates when formData changes (for edit mode only)
  useEffect(() => {
    if (formMode === "edit") {
      if (dateAdministeredRef.current && dateAdministeredRef.current._flatpickr) {
        const picker = dateAdministeredRef.current._flatpickr;
        const currentDate = picker.input.value;
        if (v.date_administered && currentDate !== v.date_administered) {
          picker.setDate(v.date_administered, false);
        }
      }
      if (nextDueDateRef.current && nextDueDateRef.current._flatpickr) {
        const picker = nextDueDateRef.current._flatpickr;
        const currentDate = picker.input.value;
        if (v.next_due_date && currentDate !== v.next_due_date) {
          picker.setDate(v.next_due_date, false);
        }
      }
    }
  }, [formMode, formData.date_administered, formData.next_due_date]);

  return (
    <div className="pet-form-container" style={{ padding: 0, background: "transparent" }}>
      {/* Card with gradient header */}
      <div style={{
        background: "#fff",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        border: "1px solid #E5E7EB"
      }}>
        {/* Gradient Header */}
        <div style={{
          background: `linear-gradient(135deg, ${userTypeColor} 0%, ${userTypeColor}dd 100%)`,
          padding: "20px 24px",
          color: "#fff"
        }}>
          <h2 style={{
            margin: 0,
            fontSize: "20px",
            fontWeight: 600,
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {formMode === "add" ? t("petProfile.medicalRecords.vaccinationHistory") : t("petProfile.medicalRecords.vaccinationHistory")}
          </h2>
        </div>

        {/* Form Content */}
        <div style={{ padding: "24px" }}>
          {/* Veterinarian */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              fontWeight: 500,
              marginBottom: "8px",
              fontSize: "14px",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("petProfile.medicalRecords.columns.veterinarian")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
            </label>
            <input
              type="text"
              placeholder={t("petProfile.medicalRecords.columns.veterinarian")}
              value={v.veterinarian}
              onChange={(e) => setBoth("Veterinarian", "veterinarian")(e.target.value)}
              style={{
                width: "100%",
                height: "38px",
                padding: "0 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "14px",
                color: "#111827",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => e.target.style.borderColor = userTypeColor}
              onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
            />
          </div>

          {/* Vaccine Name */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              fontWeight: 500,
              marginBottom: "8px",
              fontSize: "14px",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("petProfile.medicalRecords.columns.vaccineName")} <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="text"
              placeholder={t("petProfile.medicalRecords.columns.vaccineName")}
              value={v.vaccine_name}
              onChange={(e) => setBoth("Vaccine Name", "vaccine_name")(e.target.value)}
              style={{
                width: "100%",
                height: "38px",
                padding: "0 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "14px",
                color: "#111827",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => e.target.style.borderColor = userTypeColor}
              onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
            />
          </div>

          {/* Dates */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexDirection: isRTL ? "row-reverse" : "row" }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: "block",
                fontWeight: 500,
                marginBottom: "8px",
                fontSize: "14px",
                color: "#374151",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("petProfile.medicalRecords.columns.dateAdministered")} <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                ref={dateAdministeredRef}
                type="text"
                placeholder="YYYY-MM-DD"
                readOnly
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  color: "#111827",
                  transition: "border-color 0.2s",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
                onFocus={(e) => e.target.style.borderColor = userTypeColor}
                onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: "block",
                fontWeight: 500,
                marginBottom: "8px",
                fontSize: "14px",
                color: "#374151",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {t("petProfile.medicalRecords.columns.nextDueDate")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
              </label>
              <input
                ref={nextDueDateRef}
                type="text"
                placeholder="YYYY-MM-DD"
                readOnly
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  color: "#111827",
                  transition: "border-color 0.2s",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
                onFocus={(e) => e.target.style.borderColor = userTypeColor}
                onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
              />
            </div>
          </div>

          {/* Note */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{
              display: "block",
              fontWeight: 500,
              marginBottom: "8px",
              fontSize: "14px",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("petProfile.medicalRecords.columns.note")} <span style={{ color: '#6B7280', fontSize: '14px', fontWeight: 'normal' }}>({t("addPet.optional")})</span>
            </label>
            <textarea
              placeholder={t("petProfile.medicalRecords.columns.note")}
              value={v.note}
              onChange={(e) => setBoth("Note", "note")(e.target.value)}
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "12px 14px",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "14px",
                color: "#111827",
                resize: "vertical",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => e.target.style.borderColor = userTypeColor}
              onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: "12px", justifyContent: isRTL ? "flex-start" : "flex-end", flexDirection: isRTL ? "row-reverse" : "row" }}>
            <button
              onClick={onCancel}
              style={{
                background: "#fff",
                border: "1px solid #D1D5DB",
                borderRadius: "10px",
                padding: "10px 24px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                color: "#374151",
                cursor: "pointer",
                fontSize: "14px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = "#9CA3AF";
                e.target.style.background = "#F9FAFB";
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = "#D1D5DB";
                e.target.style.background = "#fff";
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              style={{
                background: userTypeColor,
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                padding: "10px 24px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "14px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = getHoverColor(userTypeColor);
              }}
              onMouseLeave={(e) => {
                e.target.style.background = userTypeColor;
              }}
            >
              {formMode === "add" ? t("addPet.save") : t("addPet.save")}
            </button>
          </div>

          {/* Validation errors */}
          {errors && Object.keys(errors).length > 0 && (
            <div style={{ marginTop: "16px" }}>
              {errors._global && errors._global.map((m, i) => (
                <div key={i} style={{ color: "#EF4444", fontSize: "14px", marginBottom: "8px" }}>{m}</div>
              ))}
              {Object.keys(errors).filter(k => k !== '_global').map((field) => (
                errors[field].map((msg, idx) => (
                  <div key={`${field}-${idx}`} style={{ color: "#EF4444", fontSize: "14px", marginBottom: "8px" }}>{msg}</div>
                ))
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default VaccinationForm;
